import { GridApi } from '@ag-grid-community/core';
import { deburr, isEmpty, isEqual, isNil, orderBy } from 'lodash';
import type { TenantConfigViewItem } from 'src/dao/tenantConfigClient';
import { flattenConfigItems } from 'src/utils/Pivot/Flatten';

//#region Visiblity util functions

export function itemVisible(item: TenantConfigViewItem) {
  return !isNil(item?.visible) ? item.visible : !isNil(item?.hidden) ? !item.hidden : true;
}

/**
 * TODO: better function name to better determine function purpose -> itemAndChildrenVisible
 * Filtered actions should read "checked" only when every displayed child is visible.
 * */
export function itemVisibleInFilter(item: TenantConfigViewItem): boolean {
  if (item.columns && item.columns.length > 0) {
    // If the parent is hidden, treat the whole group as not visible.
    // This fixes single-version items: if the parent is hidden but the child has no
    // visibility set, it was wrongly calculated here as visible because the child is visible by default
    // so checking it would not check the parent, but uncheck it again, moving it to the bottom in error
    if (!itemVisible(item)) {
      return false;
    }
    return item.columns.every((child) => itemVisible(child));
  }

  return itemVisible(item);
}

/**
 * Set visibility flags on an item (optionally propagating to children) without reordering.
 * Some configs still use `hidden` instead of `visible`, so keep both in sync here.
 */
export function setItemVisibility(
  item: TenantConfigViewItem,
  isVisible: boolean,
  propagateChildren = false,
): TenantConfigViewItem {
  const updated: TenantConfigViewItem = {
    ...item,
    visible: isVisible,
  };

  if ('hidden' in item) {
    updated.hidden = !isVisible;
  }

  if (item.columns) {
    updated.columns = item.columns.map((child) =>
      propagateChildren ? setItemVisibility(child, isVisible, true) : { ...child },
    );
  }

  return updated;
}

/** Apply show/hide only to the currently filtered tree, then recompute each parent from its full child set. */
export function applyVisibilityToFilteredItems(
  baseConfig: TenantConfigViewItem[],
  filteredConfig: TenantConfigViewItem[],
  isVisible: boolean,
): TenantConfigViewItem[] {
  if (filteredConfig.length === 0) {
    return baseConfig;
  }

  // Key by dataIndex+text so duplicate dataIndex columns (e.g. "Stylecolor" vs "Stylecolor ID")
  // are treated as distinct and dont toggle each other
  const itemKey = (i: TenantConfigViewItem) => `${i.dataIndex ?? ''}::${i.text ?? ''}`;
  const filteredByKey = new Map(filteredConfig.map((item) => [itemKey(item), item]));

  return baseConfig.map((item) => {
    const filteredItem = filteredByKey.get(itemKey(item));

    if (!filteredItem) {
      return item;
    }

    if (item.columns && item.columns.length > 0 && filteredItem.columns && filteredItem.columns.length > 0) {
      const updatedChildren = applyVisibilityToFilteredItems(item.columns, filteredItem.columns, isVisible);
      const anyVisible = updatedChildren.some((child) => itemVisible(child));
      const updatedParent: TenantConfigViewItem = {
        ...item,
        columns: updatedChildren,
        visible: anyVisible,
      };

      if ('hidden' in updatedParent) {
        updatedParent.hidden = !anyVisible;
      }

      return updatedParent;
    }

    return setItemVisibility(item, isVisible);
  });
}

//#endregion

//#region Search util functions

function getItemLabel(item: TenantConfigViewItem): string {
  return (item.viewConfiguratorName ?? item.text) || '';
}

export function itemMatchesSearch(item: TenantConfigViewItem, searchQuery: string, exact = false): boolean {
  const itemLabel = deburr(getItemLabel(item)).toLowerCase();
  return exact ? itemLabel === searchQuery : itemLabel.includes(searchQuery);
}

export function getSearchMatcher(searchValue: string): { query: string; exact: boolean } | undefined {
  const normalizedSearch = deburr(searchValue.trim());

  if (!normalizedSearch) {
    return undefined;
  }

  if (normalizedSearch.startsWith('=')) {
    const exactQuery = normalizedSearch.slice(1).trim().toLowerCase();
    return exactQuery ? { query: exactQuery, exact: true } : undefined;
  }

  return { query: normalizedSearch.toLowerCase(), exact: false };
}

export function getFilteredViewConfig(searchValue: string, viewConfig: TenantConfigViewItem[]) {
  // strip hideFromConfigurator at both levels up front - previously this only happened inside the
  // search-match branch below, so a hideFromConfigurator child would still show up in the wrench
  // whenever there was no search text
  const candidates = viewConfig
    .filter((item) => !item.hideFromConfigurator)
    .map((item) =>
      item.columns ? { ...item, columns: item.columns.filter((child) => !child.hideFromConfigurator) } : item,
    );
  const searchMatcher = getSearchMatcher(searchValue);

  if (!searchMatcher) {
    return candidates;
  }

  const { query, exact } = searchMatcher;

  return candidates.reduce<TenantConfigViewItem[]>((filteredItems, item) => {
    const itemMatches = itemMatchesSearch(item, query, exact);

    if (!item.columns || item.columns.length === 0) {
      if (itemMatches) {
        filteredItems.push(item);
      }

      return filteredItems;
    }

    const filteredChildren = item.columns.filter((child) => itemMatches || itemMatchesSearch(child, query, exact));

    if (!itemMatches && filteredChildren.length === 0) {
      return filteredItems;
    }

    filteredItems.push({
      ...item,
      columns: filteredChildren,
    });

    return filteredItems;
  }, []);
}

export function getFilteredLeafItems(items: TenantConfigViewItem[]): TenantConfigViewItem[] {
  return items.flatMap((item) => {
    if (item.columns && item.columns.length > 0) {
      return item.columns.filter((child) => !child.hideFromConfigurator);
    }
    return [item];
  });
}

//#endregion

//#region Misc. util functions

export function isPinned(pinned: string | undefined) {
  return pinned === 'left' ? true : false;
}

export function getConfigKey(item?: TenantConfigViewItem): string | undefined {
  return item ? (item.id ?? item.dataIndex ?? item.text ?? item.viewConfiguratorName) : undefined;
}

function getFullConfigItem(
  item: TenantConfigViewItem,
  viewConfig: TenantConfigViewItem[],
): TenantConfigViewItem | undefined {
  return viewConfig.find((configItem) => configItem.dataIndex === item.dataIndex);
}

export function getParentCheckboxState(
  item: TenantConfigViewItem,
  viewConfig: TenantConfigViewItem[],
): { checked: boolean; indeterminate: boolean } {
  // Three checks here:
  // 1. Check if doesn't have children and just return visible state
  // 2. If it has children, check if the parent has specific visible/hidden config and use that
  // 3. If it has children and no parent visible/hidden config use the children to calculate state

  // 1. no children
  if (!item.columns || item.columns.length === 0) {
    return {
      checked: itemVisible(item),
      indeterminate: false,
    };
  }

  // 2. Parent has specific config
  // this one uses `fullItem`, the same way that children are compared, so as to check if the
  // config shape has visible/hidden instead of the passed in item, which can be mutated
  const fullItem = getFullConfigItem(item, viewConfig) ?? item;
  if (!itemVisible(fullItem)) {
    return {
      checked: false,
      indeterminate: false,
    };
  }

  // 3. Parent has no specific config so base it off children state
  const fullChildren = (fullItem.columns ?? []).filter((child) => !child.hideFromConfigurator);
  const visibleChildrenCount = fullChildren.filter((child) => itemVisible(child)).length;

  return {
    checked: fullChildren.length > 0 && visibleChildrenCount === fullChildren.length,
    indeterminate: visibleChildrenCount > 0 && visibleChildrenCount < fullChildren.length,
  };
}

/** Function to apply grouping of views with groupingKey (ConfigurableGrid) */
export function applyGroupingInPlace(items: TenantConfigViewItem[]): TenantConfigViewItem[] {
  // if we already have nested columns, *don’t rebuild* groups—
  // but *do* fill in any undefined dataIndex on the headers because it messes up selection otherwise
  if (items.some((item) => Array.isArray(item.columns))) {
    return items.map((item, idx) => {
      if (Array.isArray(item.columns)) {
        return {
          ...item,
          // if dataIndex is falsy, give it a unique one
          dataIndex: item.dataIndex ?? `group-${item.groupingKey ?? 'header'}-${idx}`,
        };
      }
      return item;
    });
  }

  // build groupedColumns in one pass, grouping only items with same key that are adjacent
  const groupedColumns = items.reduce<TenantConfigViewItem[]>((groupedCols, current, idx) => {
    const key = current.groupingKey || '';
    const prev = groupedCols[groupedCols.length - 1];
    // if we've already started a group for this key, add it in
    if (prev?.columns && prev.groupingKey === key) {
      prev.columns.push(current);
    } else if (!isEmpty(key)) {
      groupedCols.push({
        ...current, // base header on this first item for now
        dataIndex: `group-${key}-${idx}`,
        text: key, // show the groupingKey as header text & configurator label
        viewConfiguratorName: key,
        columns: [current], // start the contiguous run here
      });
      // otherwise, if the next item shares this key, start a new group header
    } else {
      groupedCols.push(current);
    }

    return groupedCols;
  }, []);

  // then fix each group-header’s visible/hidden based on its children
  return groupedColumns.map((col) => {
    if (col.columns) {
      // if any child is visible, the header should be visible
      const anyVisible = col.columns.some((c) => c.visible !== false && c.hidden !== true);
      return {
        ...col,
        visible: anyVisible,
        hidden: !anyVisible,
      };
    }
    return col;
  });
}

//this function to check if the keyGroupedColumns are not matching with current column grid
export function hasColumnOrderChanged(gridApi: GridApi, groupedConfig: TenantConfigViewItem[]): boolean {
  const currentColumnState = gridApi.getColumnState();

  const flatConfig = flattenConfigItems(groupedConfig)
    .filter((col) => col.visible !== false && col.hideFromConfigurator !== true)
    .map((col) => col.id || col.dataIndex)
    .filter(Boolean);

  const visibleGridColIds = currentColumnState
    .filter((col) => !col.hide && flatConfig.includes(col.colId))
    .map((col) => col.colId);

  const filteredConfig = flatConfig.filter((id) => visibleGridColIds.includes(id));

  return !isEqual(filteredConfig, visibleGridColIds);
}

export function reorderItems(items: TenantConfigViewItem[]) {
  return orderBy(
    items,
    [
      (i) => {
        if (i.visible === false || i.hidden === true) {
          return 1;
        }
        return 0;
      },
      (i) => (i.pinned ? 0 : 1),
    ],
    ['asc', 'asc'],
  );
}

//#endregion
